/**
 * Pliable's own auth context.
 *
 * Replaces FronteggProvider for tenants using Pliable-managed auth (OIDC, etc.).
 * No Frontegg SDK loaded, no background refresh loops, no cookie-based session
 * management. Auth state comes from the JWT in sessionStorage.
 *
 * Provides the same hook shape as @frontegg/react so useAppAuth.ts can
 * consume either provider without conditional hooks.
 */
import { createContext, useContext, useMemo, useCallback, type ReactNode } from 'react';
import AuthService from '../services/auth.service';

interface PliableUser {
  id: string;
  sub: string;
  name: string;
  email: string;
  tenantId: string;
  tenantIds: string[];
  roles: string[];
  permissions: string[];
  accessToken: string;
  profilePictureUrl: string;
  verified: boolean;
  mfaEnrolled: boolean;
  activatedForTenant: boolean;
  tenants: Array<{ tenantId: string; name: string }>;
  metadata: string;
}

interface PliableAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: PliableUser | undefined;
}

interface PliableAuthActions {
  logout: () => void;
  switchTenant: (params: { tenantId: string }) => void;
}

interface PliableTenantsState {
  tenants: Array<{ tenantId: string; name: string }>;
  loading: boolean;
}

const PliableAuthContext = createContext<{
  auth: PliableAuthState;
  actions: PliableAuthActions;
  tenantsState: PliableTenantsState;
} | null>(null);

function decodeJwt(token: string): any {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

export function PliableAuthProvider({ children }: { children: ReactNode }) {
  const authService = AuthService.getInstance();
  const token = authService.handoffToken;
  const authState = authService.getAuthState();

  const auth = useMemo<PliableAuthState>(() => {
    if (!token || !authState) {
      return { isAuthenticated: false, isLoading: false, user: undefined };
    }

    // Check expiry
    const claims = decodeJwt(token);
    if (claims.exp && claims.exp * 1000 < Date.now()) {
      return { isAuthenticated: false, isLoading: false, user: undefined };
    }

    return {
      isAuthenticated: true,
      isLoading: false,
      user: {
        id: authState.sub,
        sub: authState.sub,
        name: authState.name,
        email: authState.email,
        tenantId: authState.tenant_id,
        tenantIds: [authState.tenant_id],
        roles: authState.roles || [],
        permissions: [],
        accessToken: authState.access_token,
        profilePictureUrl: authState.profile_img_url || '',
        verified: true,
        mfaEnrolled: false,
        activatedForTenant: true,
        tenants: authState.tenants || [],
        metadata: '',
      },
    };
  }, [token, authState]);

  const actions = useMemo<PliableAuthActions>(() => ({
    logout: () => {
      sessionStorage.removeItem('plb_auth_state');
      sessionStorage.removeItem('plb_handoff_token');
      authService.handoffToken = null;
      authService.plbAuthState = null;
      if (authService.oidcLogoutUrl) {
        window.location.href = authService.oidcLogoutUrl;
      } else {
        window.location.replace('/login');
      }
    },
    switchTenant: () => {
      // Tenant switching not supported in OIDC mode — single tenant per domain
    },
  }), [authService]);

  const tenantsState = useMemo<PliableTenantsState>(() => ({
    tenants: authState?.tenants?.length
      ? authState.tenants
      : authState ? [{ tenantId: authState.tenant_id, name: authState.tenant_name || authState.tenant_id }] : [],
    loading: false,
  }), [authState]);

  return (
    <PliableAuthContext.Provider value={{ auth, actions, tenantsState }}>
      {children}
    </PliableAuthContext.Provider>
  );
}

export function usePliableAuth(): PliableAuthState {
  const ctx = useContext(PliableAuthContext);
  if (!ctx) throw new Error('usePliableAuth must be used within PliableAuthProvider');
  return ctx.auth;
}

export function usePliableAuthActions(): PliableAuthActions {
  const ctx = useContext(PliableAuthContext);
  if (!ctx) throw new Error('usePliableAuthActions must be used within PliableAuthProvider');
  return ctx.actions;
}

export function usePliableTenantsState(): PliableTenantsState {
  const ctx = useContext(PliableAuthContext);
  if (!ctx) throw new Error('usePliableTenantsState must be used within PliableAuthProvider');
  return ctx.tenantsState;
}
